import { useEffect, useMemo, useRef, useState } from 'react';

import { type LoginTrail as TrailOptions } from '@/lib/login/loginDesign';

/**
 * 커서 꼬리 · 알갱이 사슬 — 로그인 화면판.
 *
 * <h2>어디서 왔나</h2>
 * 시안 칸의 `EmitterTrail`(디자인 실험실)을 그대로 옮긴 것이다. 원본은 Codrops 의
 * `cursors-emitter`(Marlène Bruhat) 넷 중 <b>둘째 Trail</b> — 커서를 따라가는 점 하나 뒤로
 * 점 수백 개가 사슬로 딸려 온다. 점마다 「내 앞 점이 조금 전에 있던 자리」로 `delta` 만큼
 * 다가가는 것이 전부인데, 그 지연이 쌓이면 굵은 띠가 휘어지며 늘어졌다 오므라든다.
 *
 * <p>데모 `https://tympanus.net/Development/EmitterCursor/index2.html` ·
 * 기사 `https://tympanus.net/codrops/2022/08/24/custom-svg-cursors-with-an-interactive-emitter-effect/`
 *
 * <h2>실험실판과 다르게 한 것 셋</h2>
 * <ul>
 *   <li><b>손가락을 받지 않는다</b>({@code pointer-events:none}). 이 칸은 화면 전체를 덮으므로,
 *       받으면 아이디 칸을 누를 수가 없다. 그래서 커서 자리는 <b>창</b>에서 읽는다 — 어차피
 *       칸이 곧 창이라 값도 같다.</li>
 *   <li><b>겹친다.</b> 사진 시안 위에 얹히는 층이라 {@code opacity} 와 {@code screen} 합성으로
 *       그린다 — 그대로 칠하면 사진이 통째로 가려 무엇을 켰는지 알 수 없다.</li>
 *   <li><b>바탕은 조건부</b>다({@code solid}). 사진 시안이 아래에 없을 때만 원본의 어두운 바탕을
 *       깐다 — 있을 때 깔면 그 사진을 덮는다.</li>
 * </ul>
 *
 * <h2>알갱이는 DOM 에 직접 적는다</h2>
 * 수백 개를 프레임마다 다시 그리므로 React 로 값을 내리지 않는다 — 상태로 돌리면 한 프레임에
 * 수백 번 렌더가 된다. 원본이 d3 로 하던 「머리를 맨 위로」 정렬은 <b>역순으로 그리는 것</b>으로
 * 대신했다(묶음 하나를 통째로 아끼고 결과는 같다).
 */

/** 원본 `cursor2.js` 의 바탕색. 아래에 사진이 없을 때만 깐다. */
const BACK = '#181825';

export const LOGIN_TRAIL_STYLES = `
.ltx{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:2}
.ltx svg{position:absolute;inset:0;width:100%;height:100%;display:block}
`;

export function LoginTrail({ options, solid }: { options: TrailOptions; solid: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<(SVGCircleElement | null)[]>([]);

  const { count, divisor, color1, color2, opacity } = options;

  /* 그리는 고리가 읽는 값. 만질 때마다 고리를 다시 세우면 꼬리가 그 순간 한 점으로 모인다. */
  const params = useRef({ speed: options.speed, delta: options.delta });
  params.current = { speed: options.speed, delta: options.delta };

  /** 칸 대각선에서 낸 반지름. 크기가 바뀌면 따라온다. */
  const [radius, setRadius] = useState(120);

  /* 머리가 맨 위에 오도록 «거꾸로» 그린다(원본의 d3 desc 정렬 자리). 사슬 계산은 여전히
     0→n 순서라, 그리는 차례와 사슬 차례를 갈라 둔다. */
  const drawOrder = useMemo(() => Array.from({ length: count }, (_, i) => count - 1 - i), [count]);

  /** 그라디언트 id 는 문서 안에서 유일해야 한다 — 미리보기와 진짜 화면이 한 문서에 설 수 있다. */
  const gradientId = useMemo(() => `ltx-grad-${Math.random().toString(36).slice(2, 9)}`, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    /* <b>레이아웃 크기</b>로 잰다({@code offsetWidth}). 설정 화면의 미리보기는 판 전체를
       {@code transform: scale} 로 줄여 놓는데, {@code getBoundingClientRect} 는 그 «줄어든» 크기를
       주므로 SVG 좌표(줄이기 전 좌표)와 어긋난다 — 알갱이가 커서에서 비켜 앉는다. */
    const size = { w: 1, h: 1 };
    const measure = () => {
      const w = host.offsetWidth;
      const h = host.offsetHeight;
      if (!w || !h) return;
      size.w = w;
      size.h = h;
      setRadius(Math.ceil(Math.hypot(w, h)) / divisor);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(host);

    const mouse = { x: size.w / 2, y: size.h / 2 };
    const pos = { x: mouse.x, y: mouse.y };
    const points = Array.from({ length: count }, () => ({ x: pos.x, y: pos.y }));

    /* 창에서 듣는다. 이 칸은 `pointer-events:none` 이라 제 위에서 일어난 일을 듣지 못한다 —
       그래도 칸 기준 좌표로 옮겨 둔다: 설정 화면의 미리보기는 칸이 창의 일부일 뿐이다.
       거기서는 판이 줄어 있으므로, 화면에서 잰 거리를 <b>줄이기 전 좌표로 되돌린다</b>. */
    const onMove = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const k = host.offsetWidth / r.width;
      mouse.x = (e.clientX - r.left) * k;
      mouse.y = (e.clientY - r.top) * k;
    };
    window.addEventListener('pointermove', onMove, { passive: true });

    let visible = false;
    const io = new IntersectionObserver((es) => {
      visible = es.some((e) => e.isIntersecting);
    }, { rootMargin: '100px' });
    io.observe(host);

    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!visible || document.hidden) return;

      const { speed: k, delta: d } = params.current;
      pos.x += (mouse.x - pos.x) * k;
      pos.y += (mouse.y - pos.y) * k;

      /* 원본 setParticles 그대로 — «다음 점의 «아직 안 고친» 자리» 로 당긴다. 그 한 프레임
         차이가 사슬을 만든다. 마지막 점은 0 번을 보는데, 0 번은 이미 이 프레임에 머리로
         옮겨 간 뒤라 꼬리 끝이 머리 쪽으로 한 번 더 당겨진다. 이것도 원본 그대로다. */
      const trail = { x: pos.x, y: pos.y };
      for (let i = 0; i < points.length; i++) {
        const point = points[i]!;
        const next = points[i + 1] ?? points[0]!;
        const nx = next.x;
        const ny = next.y;
        point.x = trail.x;
        point.y = trail.y;
        const node = nodesRef.current[i];
        if (node) {
          node.setAttribute('cx', String(trail.x));
          node.setAttribute('cy', String(trail.y));
        }
        trail.x += (nx - point.x) * d;
        trail.y += (ny - point.y) * d;
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
    };
  }, [count, divisor]);

  return (
    <div
      className="ltx"
      ref={hostRef}
      style={{
        background: solid ? BACK : 'transparent',
        opacity,
        // 사진 위에서는 «빛» 으로 얹힌다. 혼자 설 때는 원본처럼 그대로 칠한다.
        mixBlendMode: solid ? 'normal' : 'screen',
      }}
    >
      <svg>
        <defs>
          <linearGradient id={gradientId}>
            <stop offset="0%" stopColor={color1} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
        </defs>
        {drawOrder.map((i) => (
          <circle
            key={i}
            ref={(el) => {
              nodesRef.current[i] = el;
            }}
            r={radius}
            cx={0}
            cy={0}
            fill={`url(#${gradientId})`}
          />
        ))}
      </svg>
    </div>
  );
}
